import { Check, TrendingUp } from "lucide-react";
import { Reveal, SectionHead, Avatar, ArrowDown } from "../components/bits";

const BEFORE_TAGS = ["Overdue", "Lapsed", "Unbooked"];
const AFTER_TAGS = ["Follow-up sent ✓", "Client reactivated ✓", "Appointment booked ✓", "Revenue recovered ↑"];

const ClientRow = ({ name, tag, active, i }) => (
    <div
        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${
            active ? "border-gold/35 bg-charcoal" : "border-linen bg-ivory"
        }`}
        style={{ opacity: active ? 1 : 1 - i * 0.12 }}
    >
        <Avatar initials={name[0]} tone={active ? "bg-blush text-ink" : "bg-sand text-mute"} />
        <div className="min-w-0 flex-1">
            <div className={`truncate font-sans text-[11px] font-medium ${active ? "text-cream" : "text-ink"}`}>{name}</div>
            <div className={`font-mono text-[7px] uppercase tracking-[0.14em] ${active ? "text-gold" : "text-faint"}`}>{tag}</div>
        </div>
        {active && <Check className="h-3.5 w-3.5 shrink-0 text-gold" strokeWidth={2.5} />}
    </div>
);

export const BeforeAfter = () => (
    <section data-testid="before-after" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHead eyebrow="Before / after" title="The same database. A different outcome." align="center" />

            <Reveal delay={0.15} className="mt-12">
                <div className="relative grid overflow-hidden rounded-3xl border border-linen lg:grid-cols-2">
                    <div className="bg-sand/70 p-6 sm:p-9">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-faint">Before</span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-faint">Client database</span>
                        </div>
                        <h3 className="mt-3 font-serif text-2xl uppercase tracking-tight text-ink/70">Untapped opportunity</h3>
                        <div className="mt-6 space-y-2.5">
                            {["Sarah M.", "Elena R.", "Priya K."].map((n, i) => (
                                <ClientRow key={n} name={n} tag={BEFORE_TAGS[i]} i={i} />
                            ))}
                            <div className="flex items-center justify-center gap-2 pt-1 font-mono text-[8px] uppercase tracking-[0.2em] text-faint">
                                + hundreds more sitting idle
                            </div>
                        </div>
                    </div>

                    <div className="grain relative bg-ink p-6 sm:p-9">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">After</span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-cream/50">Active client database</span>
                        </div>
                        <h3 className="mt-3 font-serif text-2xl uppercase tracking-tight text-cream">Working for you</h3>
                        <div className="mt-6 space-y-2.5">
                            {["Sarah M.", "Elena R.", "Priya K."].map((n, i) => (
                                <ClientRow key={n} name={n} tag={AFTER_TAGS[i]} active i={0} />
                            ))}
                            <div className="flex items-center justify-center gap-2 pt-1 font-mono text-[8px] uppercase tracking-[0.2em] text-gold">
                                Revenue recovered <TrendingUp className="h-3 w-3" />
                            </div>
                        </div>
                    </div>

                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-24 w-24 -translate-x-1/2 -translate-y-1/2 lg:block">
                        <span
                            className="absolute flex items-center gap-2 rounded-full border border-gold/40 bg-obsidian px-3.5 py-2 shadow-xl"
                            style={{ animation: "travelX 4.5s linear infinite", left: "-140px", width: "max-content" }}
                        >
                            <Avatar initials="S" tone="bg-blush text-ink" />
                            <span className="font-mono text-[8px] uppercase tracking-[0.16em] text-cream">Reactivating…</span>
                        </span>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-16">
                <div className="mx-auto max-w-3xl">
                    <div className="text-center font-mono text-[10px] uppercase tracking-[0.24em] text-gold">The reactivation funnel</div>
                    <div className="mt-6 flex flex-col items-center" data-testid="reactivation-funnel">
                        {["Database", "Segmented", "Contacted", "Responded", "Booked"].map((t, i) => (
                            <div key={t} className="flex w-full flex-col items-center">
                                {i > 0 && <ArrowDown className="my-1.5" />}
                                <div
                                    className={`flex items-center justify-between rounded-full border px-6 py-3 transition-colors duration-500 ${
                                        i === 4
                                            ? "border-gold/60 bg-ink text-cream"
                                            : "border-linen bg-ivory text-ink"
                                    }`}
                                    style={{ width: `${100 - i * 13}%` }}
                                >
                                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-60">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="font-sans text-[12px] font-medium uppercase tracking-[0.14em]">{t}</span>
                                    {i === 4 ? (
                                        <Check className="h-3.5 w-3.5 text-gold" strokeWidth={2.5} />
                                    ) : (
                                        <span className={`h-1.5 w-1.5 rounded-full ${i === 0 ? "bg-faint" : "bg-rose"}`} />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-5 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-faint">
                        Process illustration — no invented percentages, only your real numbers
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);
